// Shuffling, seating and team building. No Vue in here.

export function shuffle(list) {
    const result = list.slice();
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

function pick(list) {
    return list[Math.floor(Math.random() * list.length)];
}

// The tables beside a table (left, right) and behind it (front, back). A row's
// last table is not next to the first table of the row below.
function adjacent(index, rows, cols) {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const beside = [];
    const behind = [];
    if (col > 0) beside.push(index - 1);
    if (col < cols - 1) beside.push(index + 1);
    if (row > 0) behind.push(index - cols);
    if (row < rows - 1) behind.push(index + cols);
    return { beside, behind };
}

// How far a seating is from what was asked for:
// separated - pairs of the same separation group sitting next to each other
// apart     - members of a partner group with none of their group next to them
// cost      - what the search brings down; side by side beats one behind the other
// trouble   - the tables worth moving
function evaluate(seats, byId, partnerSizes, room) {
    const trouble = new Set();
    let separated = 0;
    let apart = 0;
    let cost = 0;
    Object.keys(seats).forEach(key => {
        const index = Number(key);
        const student = byId[seats[index]];
        const { beside, behind } = adjacent(index, room.rows, room.cols);
        const sameGroup = (other, group) => seats[other] !== undefined && byId[seats[other]][group] === student[group];

        if (student.blockingGroup) {
            beside.concat(behind).forEach(other => {
                if (other > index && sameGroup(other, 'blockingGroup')) {
                    separated += 1;
                    cost += 10;
                    trouble.add(index);
                    trouble.add(other);
                }
            });
        }
        if (student.partnerGroup && partnerSizes[student.partnerGroup] > 1) {
            if (beside.some(other => sameGroup(other, 'partnerGroup'))) return;
            trouble.add(index);
            if (behind.some(other => sameGroup(other, 'partnerGroup'))) {
                cost += 1;
            } else {
                cost += 4;
                apart += 1;
            }
        }
    });
    return { cost, separated, apart, trouble: [...trouble] };
}

// A random seat for everyone. Separation groups are kept apart and partner
// groups together where the room allows it. `fixed` ({ tableIndex: studentId })
// are seats that stay as they are. Returns null when there are fewer tables
// than students, otherwise { seats: { tableIndex: studentId }, separated, apart }
// with what it couldn't solve.
export function generateSeating(students, room, fixed = {}) {
    const free = room.tables.filter(t => t.available).map(t => t.index);
    if (free.length < students.length) return null;

    const byId = {};
    const partnerSizes = {};
    students.forEach(s => {
        byId[s.id] = { blockingGroup: s.blockingGroup || 0, partnerGroup: s.partnerGroup || 0 };
        if (s.partnerGroup) partnerSizes[s.partnerGroup] = (partnerSizes[s.partnerGroup] || 0) + 1;
    });

    const fixedIds = Object.values(fixed);
    const loose = students.filter(s => !fixedIds.includes(s.id));
    // The others start on the first tables that aren't taken. A repair may
    // also use a table that is still empty, if someone sits better there.
    const open = free.filter(index => fixed[index] === undefined);
    const isOpen = {};
    open.forEach(index => { isOpen[index] = true; });

    let best = null;
    for (let attempt = 0; attempt < 60; attempt++) {
        const seats = { ...fixed };
        shuffle(loose).forEach((student, i) => { seats[open[i]] = student.id; });
        const swap = (a, b) => {
            const first = seats[a];
            const second = seats[b];
            if (second === undefined) delete seats[a]; else seats[a] = second;
            if (first === undefined) delete seats[b]; else seats[b] = first;
        };
        let state = evaluate(seats, byId, partnerSizes, room);

        // Repair: move someone who sits wrong, and keep the move if it doesn't make things worse.
        for (let step = 0; step < 300 && state.cost > 0; step++) {
            const candidates = state.trouble.filter(index => isOpen[index]);
            if (candidates.length === 0) break;
            const from = pick(candidates);
            const group = byId[seats[from]].partnerGroup;
            let to;
            // Half of the time, aim for a table next to someone of the same partner group
            if (group && Math.random() < 0.5) {
                const mates = Object.keys(seats).map(Number).filter(index => index !== from && byId[seats[index]].partnerGroup === group);
                if (mates.length > 0) {
                    const { beside, behind } = adjacent(pick(mates), room.rows, room.cols);
                    const spots = beside.concat(behind).filter(index => isOpen[index] && index !== from);
                    if (spots.length > 0) to = pick(spots);
                }
            }
            if (to === undefined) to = pick(open);
            if (from === to) continue;
            const toEmpty = seats[to] === undefined;
            swap(from, to);
            const after = evaluate(seats, byId, partnerSizes, room);
            // Onto an empty table only for a real gain, so the class doesn't drift apart
            if (after.cost < state.cost || (!toEmpty && after.cost === state.cost)) {
                state = after;
            } else {
                swap(from, to);
            }
        }

        if (!best || state.cost < best.cost) {
            best = { seats, cost: state.cost, separated: state.separated, apart: state.apart };
        }
        if (best.cost === 0) break;
    }
    return { seats: best.seats, separated: best.separated, apart: best.apart };
}

// How many teams `present` people make. mode 'count' makes `value` teams;
// mode 'size' aims for teams of `value` and would rather make one team larger
// than leave someone alone (21 people in pairs: 9 pairs and a team of 3).
export function teamCount(present, mode, value) {
    if (present === 0) return 0;
    if (mode === 'count') return Math.max(1, Math.min(value, present));
    if (value <= 1) return present;
    const more = Math.ceil(present / value);
    const smallest = Math.floor(present / more);
    return smallest >= Math.max(2, value - 1) ? more : Math.max(1, Math.floor(present / value));
}

// Random teams whose sizes differ by one at most. Partner groups go into one
// team, separation groups into different ones. Returns { teams, conflicts, split }:
// conflicts are teams' extra members of one separation group, split the partner
// groups that didn't fit into one team.
export function buildTeams(students, mode, value) {
    if (students.length === 0) return { teams: [], conflicts: 0, split: 0 };
    const count = teamCount(students.length, mode, value);
    const sizes = Array.from({ length: count }, (_, i) => Math.floor(students.length / count) + (i < students.length % count ? 1 : 0));

    const partners = {};
    students.forEach(student => {
        if (!student.partnerGroup) return;
        partners[student.partnerGroup] = (partners[student.partnerGroup] || []).concat(student);
    });
    const units = Object.values(partners).filter(unit => unit.length > 1);
    const inUnit = units.flat();

    let best = null;
    for (let attempt = 0; attempt < 200; attempt++) {
        const room = shuffle(sizes);
        const teams = Array.from({ length: count }, () => []);
        const space = index => room[index] - teams[index].length;
        const indices = teams.map((_, index) => index);
        const singles = students.filter(student => !inUnit.includes(student));
        let split = 0;

        // Partner groups first, the largest before the others
        shuffle(units).sort((a, b) => b.length - a.length).forEach(unit => {
            const fits = indices.filter(index => space(index) >= unit.length);
            if (fits.length > 0) {
                teams[pick(fits)].push(...unit);
            } else {
                split += 1;
                singles.push(...unit);
            }
        });
        // Everyone else, into a team without their separation group if there is one
        shuffle(singles).forEach(student => {
            const open = indices.filter(index => space(index) > 0);
            const calm = open.filter(index => !student.blockingGroup || !teams[index].some(other => other.blockingGroup === student.blockingGroup));
            teams[pick(calm.length > 0 ? calm : open)].push(student);
        });

        let conflicts = 0;
        teams.forEach(team => {
            const seen = {};
            team.forEach(student => {
                if (!student.blockingGroup) return;
                if (seen[student.blockingGroup]) conflicts += 1;
                seen[student.blockingGroup] = true;
            });
        });
        if (!best || conflicts + split < best.conflicts + best.split) best = { teams, conflicts, split };
        if (conflicts + split === 0) break;
    }
    return best;
}
